import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, FileText, ExternalLink, Phone, Mail, ShieldCheck, AlertCircle,
  Download, ScrollText, MessageSquare, Building2, ChevronRight, FileCheck2, BadgeCheck,
  HeartHandshake, Info, CalendarDays,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { CLINIC } from "../lib/clinic";
import { useSEO } from "../lib/useSEO";
import { trackEvent } from "../lib/analytics";

const BDP_REG = "0004211";
const ENTITY = "Dental Council, Lda";
const HISCOX_POLICY = "2514673";
const PHONE_INTERMEDIATION = "+351 966 656 631";
const PHONE_INTERMEDIATION_TEL = "tel:+351966656631";

const DOCS = [
  {
    name: "Ficha de Informação Pré-Contratual (FINE)",
    href: "/docs/fine_credito.pdf",
    note: "Documento padrão entregue pelo mutuante antes da subscrição.",
    icon: FileCheck2,
  },
  {
    name: "Modelo de Contrato de Intermediação",
    href: "/docs/contrato_intermediacao.pdf",
    note: "Termos e condições do serviço de intermediação.",
    icon: ScrollText,
  },
  {
    name: `Apólice de Responsabilidade Civil — Hiscox (Apólice n.º ${HISCOX_POLICY})`,
    href: "/docs/hiscox_2514673.pdf",
    note: "Comprovativo de seguro obrigatório do intermediário.",
    icon: ShieldCheck,
  },
];

function SectionTitle({ icon: Icon, children, tooltip }) {
  return (
    <h2 className="font-serif text-[28px] md:text-[32px] leading-tight text-brand-500 flex items-center gap-3">
      <span className="w-10 h-10 rounded-full bg-brand-50 text-brand-500 inline-flex items-center justify-center shrink-0">
        <Icon size={18} strokeWidth={1.8} />
      </span>
      <span className="inline-flex items-center">
        {children}
        {tooltip}
      </span>
    </h2>
  );
}

function Card({ children, className = "", testid, id }) {
  return (
    <section
      id={id}
      data-testid={testid}
      className={`bg-white rounded-2xl shadow-[0_1px_2px_rgba(15,62,104,0.04),0_8px_24px_-12px_rgba(15,62,104,0.08)] border border-slate-200/60 p-7 md:p-10 ${className}`}
    >
      {children}
    </section>
  );
}

/**
 * Accessible info tooltip — hover on desktop, click/tap on mobile, ESC to close.
 * Uses aria-describedby + role="tooltip" for screen readers.
 */
function InfoTooltip({ id, label, text }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <span ref={wrapRef} className="relative inline-flex items-center">
      <button
        type="button"
        aria-label={label}
        aria-describedby={id}
        aria-expanded={open}
        data-testid={`info-btn-${id}`}
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="ml-1 w-7 h-7 inline-flex items-center justify-center rounded-full text-brand-500 hover:bg-brand-50 hover:text-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-300 transition-colors"
      >
        <Info size={15} strokeWidth={2} />
      </button>
      <span
        id={id}
        role="tooltip"
        className={`absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[260px] z-30 rounded-lg bg-brand-700 text-white text-[12.5px] leading-relaxed px-3.5 py-2.5 shadow-xl transition-opacity duration-150 pointer-events-none ${open ? "opacity-100" : "opacity-0"}`}
      >
        <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rotate-45 bg-brand-700" aria-hidden="true" />
        {text}
      </span>
    </span>
  );
}

export default function FinanciamentoPage() {
  useSEO({
    title: "Financiamento e Intermediação de Crédito — Instituto Clínico do Barlavento",
    description: `Intermediação de crédito prestada por ${ENTITY} (Reg. BdP ${BDP_REG}). Consulte as condições pré-contratuais, FINE e apólice de responsabilidade civil.`,
  });

  const trackDoc = (name, href) => {
    const filename = href ? href.split("/").pop() : name;
    trackEvent("download_document", { source: "financiamento_page", document: name, filename });
  };

  return (
    <div data-testid="financing-page" className="pt-28 pb-24 min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50/60">
      <div className="container-icb max-w-6xl">
        {/* TOP NAV */}
        <Link to="/">
          <Button variant="outline" className="mb-7 bg-white/70 backdrop-blur" data-testid="financing-back">
            <ArrowLeft size={14} className="mr-2" /> Voltar à página inicial
          </Button>
        </Link>

        {/* HEADER */}
        <header className="mb-10">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-500 text-white text-[11px] font-semibold tracking-[0.18em] uppercase shadow-sm">
              <BadgeCheck size={14} /> Reg. BdP <span data-testid="bdp-registration" className="font-bold">{BDP_REG}</span>
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 text-[11px] font-medium tracking-wider uppercase">
              Categoria: Título acessório
            </span>
          </div>

          <h1 className="font-serif text-[44px] md:text-[56px] leading-[1.05] tracking-tight text-brand-500 mb-4" data-testid="financing-title">
            Informação sobre Financiamento
          </h1>
          <p className="text-slate-600 leading-relaxed max-w-3xl text-[17px] md:text-[18px]">
            O Instituto Clínico do Barlavento <strong className="text-brand-500">não concede crédito</strong>. A intermediação de crédito é prestada por <strong className="text-brand-500">{ENTITY}</strong>, registada no Banco de Portugal sob o n.º <strong>{BDP_REG}</strong>.
          </p>
          <p className="text-xs text-slate-400 mt-5 tracking-wider uppercase">
            Última atualização: {new Date().toLocaleDateString("pt-PT", { month: "long", year: "numeric" })}
          </p>
        </header>

        {/* GRID 2 COLUMNS */}
        <div className="grid lg:grid-cols-3 gap-7 lg:gap-9 items-start">
          {/* LEFT — 2/3 */}
          <div className="lg:col-span-2 space-y-7">
            {/* MUTUANTES */}
            <Card testid="financing-lenders">
              <SectionTitle icon={Building2}>Mutuantes vinculados</SectionTitle>
              <p className="text-slate-600 leading-relaxed mt-5 text-[15.5px]">
                Contratos de vinculação com:
              </p>
              <ul className="mt-4 space-y-2.5">
                {[
                  "ABANCA Servicios Financieros, E.F.C., S.A. — Sucursal em Portugal",
                  "BBVA, Instituição Financeira de Crédito S.A.",
                ].map((m) => (
                  <li key={m} className="flex items-start gap-3 text-slate-700 leading-relaxed">
                    <ChevronRight size={16} className="text-gold-400 mt-1 shrink-0" />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </Card>

            {/* DOCUMENTOS */}
            <Card testid="financing-docs-card" className="scroll-mt-28" id="docs">
              <SectionTitle icon={FileText}>Documentos pré-contratuais</SectionTitle>
              <p className="text-slate-600 leading-relaxed mt-5 text-[15.5px] mb-6">
                Antes de qualquer proposta disponibilizamos a Ficha de Informação Pré-Contratual (FINE) e demais documentos.
              </p>
              <ul className="space-y-3" data-testid="financing-docs">
                {DOCS.map((d) => {
                  const Icon = d.icon;
                  return (
                    <li key={d.href}>
                      <a
                        href={d.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackDoc(d.name, d.href)}
                        data-testid={`doc-${d.href.split("/").pop()}`}
                        className="group flex items-center gap-4 p-4 rounded-xl bg-slate-50/80 border border-slate-200/70 hover:border-brand-300 hover:bg-white hover:shadow-md transition-all duration-200"
                      >
                        <span className="w-11 h-11 rounded-lg bg-white border border-slate-200 text-brand-500 inline-flex items-center justify-center shrink-0 group-hover:bg-brand-500 group-hover:text-white group-hover:border-brand-500 transition-colors">
                          <Icon size={20} strokeWidth={1.6} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-800 text-[14.5px] leading-snug truncate group-hover:text-brand-500 transition-colors">{d.name}</p>
                          <p className="text-[12.5px] text-slate-500 mt-0.5 leading-snug">{d.note}</p>
                        </div>
                        <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-500 text-white text-[12px] font-medium group-hover:bg-gold-500 group-hover:text-brand-700 transition-colors shrink-0">
                          <Download size={14} /> PDF
                        </span>
                        <Download size={18} className="sm:hidden text-brand-500 shrink-0" />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </Card>

            {/* APROVAÇÃO */}
            <Card testid="financing-approval">
              <SectionTitle icon={ShieldCheck}>Aprovação</SectionTitle>
              <p className="text-slate-700 leading-[1.75] mt-5 text-[15.5px]">
                A aprovação do crédito está <strong>sujeita à avaliação e aprovação da instituição mutuante</strong>. As condições finais são definidas pelo mutuante e sujeitas a verificação de documentação.
              </p>
            </Card>

            {/* POR QUE PEDIR CRÉDITO — informative, friendly */}
            <Card testid="financing-why-credit" id="por-que-credito">
              <SectionTitle icon={HeartHandshake}>Por que pode ser necessário pedir crédito?</SectionTitle>
              <p className="text-slate-700 leading-[1.75] mt-5 text-[15.5px]">
                Pedir financiamento é uma opção prática para quem precisa de tratar a saúde dentária sem comprometer a disponibilidade imediata de rendimento. Em muitos casos o crédito permite:
              </p>
              <ul className="mt-4 space-y-2.5">
                {[
                  "Distribuir o custo de tratamentos complexos em prestações mensais acessíveis.",
                  "Permitir o acesso a tratamentos prioritários sem adiar procedimentos necessários.",
                  "Facilitar o planeamento financeiro familiar (parcelamento sem juros em opções selecionadas).",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-700 leading-relaxed text-[15px]">
                    <ChevronRight size={16} className="text-gold-400 mt-1 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[14px] text-slate-600 leading-relaxed mt-5 pt-5 border-t border-slate-100">
                As propostas são sempre sujeitas à aprovação do mutuante e à entrega da documentação pré-contratual (FINE). Nós ajudamos a simular opções — sem qualquer compromisso.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/#marcacao"
                  onClick={() => trackEvent("click_financing", { source: "why_credit_cta_marcar" })}
                  data-testid="why-credit-cta-marcar"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-semibold text-[14px] min-h-[44px] transition-colors"
                >
                  <CalendarDays size={16} /> Marcar consulta para avaliação
                </Link>
                <a
                  href={`mailto:${CLINIC.email}`}
                  onClick={() => trackEvent("click_email", { source: "why_credit_cta_email" })}
                  data-testid="why-credit-cta-email"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white border border-slate-300 hover:border-brand-500 hover:text-brand-500 text-slate-700 font-semibold text-[14px] min-h-[44px] transition-colors"
                >
                  <Mail size={16} /> Fale connosco por email
                </a>
              </div>
            </Card>

            {/* RECLAMAÇÕES */}
            <Card testid="financing-complaints">
              <SectionTitle icon={MessageSquare}>Reclamações e canais oficiais</SectionTitle>
              <div className="mt-5 space-y-4 text-slate-700 leading-[1.75] text-[15.5px]">
                <p>
                  Saiba mais sobre os intermediários de crédito no portal oficial:{" "}
                  <a
                    href="https://clientebancario.bportugal.pt/pt-pt/intermediarios-de-credito-o-que-sao"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-500 hover:text-gold-500 underline-offset-2 hover:underline inline-flex items-center gap-1"
                    data-testid="link-bdp-info"
                  >
                    Intermediários de crédito — o que são <ExternalLink size={12} />
                  </a>
                </p>
                <p>
                  Para apresentar uma reclamação ao Banco de Portugal:{" "}
                  <a
                    href="https://clientebancario.bportugal.pt/formulario-nova-reclamacao"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-500 hover:text-gold-500 underline-offset-2 hover:underline inline-flex items-center gap-1"
                    data-testid="link-bdp-complaint"
                  >
                    Formulário de reclamação <ExternalLink size={12} />
                  </a>
                </p>
              </div>
            </Card>

            {/* SEGURO */}
            <Card testid="financing-insurance">
              <SectionTitle
                icon={ShieldCheck}
                tooltip={
                  <InfoTooltip
                    id="tooltip-insurance"
                    label="Porque temos seguro de responsabilidade civil?"
                    text="O seguro de responsabilidade civil protege os pacientes e a clínica em caso de danos relacionados com a prestação de cuidados. É um requisito contratual e regulamentar para intermediários."
                  />
                }
              >
                Seguro de Responsabilidade Civil
              </SectionTitle>
              <p className="text-slate-700 leading-[1.75] mt-5 text-[15.5px]">
                Seguradora: <strong>Hiscox, S.A. — Sucursal em Portugal</strong>. Apólice n.º <strong>{HISCOX_POLICY}</strong>. O documento da apólice encontra-se disponível na{" "}
                <a href="#docs" className="text-brand-500 hover:text-gold-500 underline-offset-2 hover:underline">secção de documentos</a> acima.
              </p>
            </Card>

            {/* AML / KYC */}
            <Card testid="financing-aml">
              <SectionTitle
                icon={ShieldCheck}
                tooltip={
                  <InfoTooltip
                    id="tooltip-aml"
                    label="O que é AML / KYC?"
                    text="Procedimentos de prevenção de branqueamento de capitais: pedimos documentos de identificação e comprovativo de morada para cumprir a legislação e proteger os clientes."
                  />
                }
              >
                Procedimentos AML / KYC
              </SectionTitle>
              <p className="text-slate-700 leading-[1.75] mt-5 text-[15.5px]">
                Procedemos à verificação de identidade dos clientes e aos controlos de prevenção de branqueamento de capitais nos termos da legislação aplicável. Documentos solicitados: <strong>Cartão de Cidadão/Passaporte, comprovativo de morada e NIF</strong>, conforme o caso.
              </p>
            </Card>

            {/* AVISO REFORÇADO */}
            <div className="bg-amber-50/70 border border-amber-200/70 rounded-2xl p-6 flex gap-4" data-testid="financing-disclaimer">
              <AlertCircle size={20} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[14px] text-slate-700 leading-relaxed">
                <strong>Aviso:</strong> Nem o Instituto Clínico do Barlavento nem a entidade intermediária habilitada concedem crédito diretamente. A intermediação de crédito é prestada por <strong>Dental Council, Lda</strong> e as propostas são processadas e aprovadas pelas instituições mutuantes (p.ex. <strong>ABANCA</strong>, <strong>BBVA</strong>). Consulte a documentação pré-contratual na{" "}
                <Link
                  to="/financiamento"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("click_financing", { source: "disclaimer_link" })}
                  className="text-brand-500 hover:text-gold-500 underline-offset-2 hover:underline font-medium"
                >
                  Página de Financiamento
                </Link>.
              </p>
            </div>
          </div>

          {/* RIGHT — STICKY CARD 1/3 */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 space-y-5">
              {/* QUICK CONTACT CARD */}
              <div className="bg-brand-500 text-white rounded-2xl p-7 shadow-xl shadow-brand-200/40 relative overflow-hidden" data-testid="financing-sidebar-contact">
                <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-gold-400/10" />
                <div className="absolute -bottom-16 -left-10 w-44 h-44 rounded-full bg-white/5" />
                <div className="relative">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-gold-300 mb-3">Contacto rápido</p>
                  <h3 className="font-serif text-2xl mb-4 leading-tight">Intermediação de crédito</h3>
                  <div className="space-y-1.5 mb-6 text-[13px] text-white/85 leading-relaxed">
                    <p><span className="text-gold-300">Entidade:</span> {ENTITY}</p>
                    <p><span className="text-gold-300">Reg. BdP:</span> {BDP_REG}</p>
                    <p><span className="text-gold-300">Categoria:</span> Título acessório</p>
                    <p><span className="text-gold-300">Responsável Técnico:</span> Tiago Miguel Pires Frazão</p>
                  </div>

                  <a
                    href={PHONE_INTERMEDIATION_TEL}
                    onClick={() => trackEvent("click_phone", { source: "financing_sidebar" })}
                    data-testid="sidebar-phone"
                    className="flex items-center gap-3 p-3.5 rounded-xl bg-white/10 hover:bg-white/15 transition-colors mb-2.5 group"
                  >
                    <span className="w-9 h-9 rounded-full bg-gold-400 text-brand-700 inline-flex items-center justify-center shrink-0">
                      <Phone size={15} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10.5px] uppercase tracking-wider text-white/60">Telefone</p>
                      <p className="font-semibold text-[15px] tracking-tight">{PHONE_INTERMEDIATION}</p>
                    </div>
                  </a>

                  <a
                    href={`mailto:${CLINIC.email}`}
                    onClick={() => trackEvent("click_email", { source: "financing_sidebar" })}
                    data-testid="sidebar-email"
                    className="flex items-center gap-3 p-3.5 rounded-xl bg-white/10 hover:bg-white/15 transition-colors group"
                  >
                    <span className="w-9 h-9 rounded-full bg-gold-400 text-brand-700 inline-flex items-center justify-center shrink-0">
                      <Mail size={15} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10.5px] uppercase tracking-wider text-white/60">Email</p>
                      <p className="font-semibold text-[13px] tracking-tight break-all">{CLINIC.email}</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* QUICK LINK TO DOCS (replaces duplicate downloads list) */}
              <a
                href="#docs"
                onClick={() => trackEvent("click_financing", { source: "sidebar_view_docs" })}
                data-testid="sidebar-view-docs"
                className="flex items-center justify-between gap-3 p-4 rounded-2xl bg-white border border-slate-200/70 shadow-sm hover:border-brand-300 hover:shadow-md transition-all group"
              >
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-brand-50 text-brand-500 inline-flex items-center justify-center shrink-0 group-hover:bg-brand-500 group-hover:text-white transition-colors">
                    <FileText size={16} />
                  </span>
                  <div>
                    <p className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Documentos</p>
                    <p className="text-[14px] font-semibold text-brand-500 leading-tight">Ver documentos</p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-400 group-hover:text-brand-500 transition-colors" />
              </a>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
